import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAppStore } from '@/stores/useAppStore';
import { useDataPrefetch } from '@/hooks/useDataPrefetch';
import { useDataMigration } from '@/hooks/useDataMigration';
import { useGamificationSync } from '@/hooks/useGamificationSync';
import { useMissionSync } from '@/hooks/useMissionSync';
import { useWaterSync } from '@/hooks/useWaterSync';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

function DataSyncComponent() {
  const { user } = useAuthStore();
  const { goals, habits, challenges, setGoals, setHabits, setChallenges } = useAppStore();
  const [fallbackTriggered, setFallbackTriggered] = useState(false);
  const [badgeCheckDone, setBadgeCheckDone] = useState(false);
  
  // 🚀 Usar prefetch inteligente ao invés de sync tradicional
  const prefetchStatus = useDataPrefetch(user?.id || null, !!user);
  
  // 📦 Executar migrações de dados automaticamente
  useDataMigration();
  
  // 🏆 Verificar badges faltantes no login (1x por sessão)
  useEffect(() => {
    const checkMissingBadges = async () => {
      if (!user?.id || badgeCheckDone) return;
      
      // Verificar se já executou nesta sessão
      const sessionKey = `badges_checked_${user.id}`;
      if (sessionStorage.getItem(sessionKey)) {
        setBadgeCheckDone(true);
        return;
      }
      
      try {
        console.log('🏆 Verificando badges faltantes...');
        
        // Buscar perfil
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single();
        
        if (!profile?.onboarding_completed) {
          console.log('ℹ️ Onboarding não completado, pulando verificação de badges');
          setBadgeCheckDone(true);
          return;
        }
        
        // Buscar badges do usuário
        const { data: userBadges } = await supabase
          .from('user_badges')
          .select('badge_id')
          .eq('user_id', user.id);
        
        const hasBadges = new Set(userBadges?.map(b => b.badge_id) || []);
        
        // Verificar badges obrigatórios
        const { badgesService } = await import('@/services/badgesService');
        
        if (!hasBadges.has('badge-onboarding')) {
          console.log('🏆 Concedendo badge de onboarding...');
          await badgesService.awardBadge(user.id, 'badge-onboarding');
        }
        
        if (!hasBadges.has('badge-wheel')) {
          console.log('🏆 Concedendo badge da Roda da Vida...');
          await badgesService.awardBadge(user.id, 'badge-wheel');
        }
        
        // Marcar como verificado nesta sessão
        sessionStorage.setItem(sessionKey, 'true');
        console.log('✅ Verificação de badges concluída');
        
      } catch (error) {
        console.error('❌ Erro ao verificar badges:', error);
      } finally {
        setBadgeCheckDone(true);
      }
    };
    
    checkMissingBadges();
  }, [user?.id, badgeCheckDone]);
  
  // Fallback: carregar dados manualmente se prefetch não trouxe nada
  useEffect(() => {
    const attemptFallbackLoad = async () => {
      if (prefetchStatus.isComplete && !fallbackTriggered && user?.id) {
        const hasData = goals.length > 0 || habits.length > 0 || challenges.length > 0;
        
        if (!hasData && prefetchStatus.errors.length === 0) {
          console.warn('⚠️ Prefetch completou mas nenhum dado foi carregado');
          console.warn('   Tentando carregar dados via fallback tradicional...');
          setFallbackTriggered(true);
          
          try {
            // Carregar hábitos
            const { data: habitsData, error: habitsError } = await supabase
              .from('user_habits')
              .select('*')
              .eq('user_id', user.id)
              .eq('active', true);
            
            if (!habitsError && habitsData) {
              const formattedHabits = habitsData.map((habit: any) => ({
                ...habit,
                pillars: habit.pillars || [habit.pilar],
                daysOfWeek: habit.days_of_week || [],
                specificDates: habit.specific_dates || [],
                restDays: habit.rest_days || [],
                history: [],
                frequency: habit.frequency === 'diaria' ? 'daily' : 
                          habit.frequency === 'semanal' ? 'weekly' : 
                          habit.frequency === 'mensal' ? 'monthly' : 'daily'
              }));
              setHabits(formattedHabits as any);
              console.log('✅ Fallback: Hábitos carregados:', formattedHabits.length);
            }
            
            // Carregar metas
            const { data: goalsData, error: goalsError } = await supabase
              .from('user_goals')
              .select('*')
              .eq('user_id', user.id)
              .neq('status', 'concluida');
            
            if (!goalsError && goalsData) {
              const formattedGoals = goalsData.map((goal: any) => ({
                ...goal,
                pillars: goal.pillars || [goal.pilar],
                miniGoals: []
              }));
              setGoals(formattedGoals as any);
              console.log('✅ Fallback: Metas carregadas:', formattedGoals.length);
            }
            
            // Carregar desafios
            const { data: challengesData, error: challengesError } = await supabase
              .from('user_challenges')
              .select('*')
              .eq('user_id', user.id)
              .eq('active', true)
              .eq('archived', false);
            
            if (!challengesError && challengesData) {
              const formattedChallenges = challengesData.map((challenge: any) => ({
                ...challenge,
                completedDates: challenge.completed_dates || [],
                journalEntries: []
              }));
              setChallenges(formattedChallenges as any);
              console.log('✅ Fallback: Desafios carregados:', formattedChallenges.length);
            }
          } catch (error) {
            console.error('❌ Erro no fallback de carregamento:', error);
            toast.error('Erro ao carregar dados. Tente fazer login novamente.');
          }
        }
      }
    };
    
    attemptFallbackLoad();
  }, [prefetchStatus.isComplete, prefetchStatus.errors, goals.length, habits.length, challenges.length, fallbackTriggered, user, setGoals, setHabits, setChallenges]);
  
  // Manter syncs específicos para realtime updates
  useGamificationSync();
  useMissionSync();
  useWaterSync();
  
  return null;
}

export function AuthInitializer() {
  const [isReady, setIsReady] = useState(false);
  const initialize = useAuthStore((state) => state.initialize);
  
  useEffect(() => {
    // Small delay to ensure all providers are ready
    const timer = setTimeout(() => {
      initialize().finally(() => {
        setIsReady(true);
      });
    }, 0);
    
    return () => clearTimeout(timer);
  }, [initialize]);

  return isReady ? <DataSyncComponent /> : null;
}
